import { useEffect, useContext } from 'react';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { Processing } from '@/api/entities/processing';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import {
  useGetCarProcessedById,
  useGenerateReportEngine,
  useGetNextDataReport,
} from '@/hooks/ai.hooks';
import {
  getColorByValue,
  initReportEngine,
  forceInitReport,
} from '@/utils/ia.utils';
import { useGetModerationEquipments } from './equipment.hooks';
import { useGetModerationDamages } from './damage.hooks';

interface ReportDataProps {
  id: string;
  showOnlyDetections?: boolean;
  showOnlyModerations?: boolean;
  equipmentClassId?: number;
}

const useReportData = ({
  id,
  showOnlyDetections = false,
  showOnlyModerations = false,
  equipmentClassId,
}: ReportDataProps) => {
  const {
    reportData,
    reportEngineData,
    setReportEngineData,
    sendLogs,
    setSendLogs,
    setDataAnnotation,
    setDataAnnotationDamage,
    setListElement,
    setListDamage,
    dataAnnotationDamage,
  } = useContext(EngineCorrectionContext);

  const { mutate: generateReport } = useGenerateReportEngine();

  const { data: carProcessed, isPending } = useGetCarProcessedById(id);

  const { data: carProcessedNext } = useGetNextDataReport(id);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  const dataCar = carProcessed?.data as Processing;

  const generateReportData = () => {
    if (
      reportEngineData.create.length === 0 &&
      reportEngineData.edit.length === 0 &&
      reportEngineData.remove.length === 0
    )
      generateReport(forceInitReport(id));
    else generateReport(reportEngineData);
    setReportEngineData(initReportEngine(id));
  };

  useEffect(() => {}, [dataAnnotationDamage]);

  useEffect(() => {
    if (sendLogs) {
      // generateReport(reportEngineData);
      setReportEngineData(initReportEngine(id));
    }
    setSendLogs(false);
  }, [reportData]);

  useEffect(() => {
    if (dataCar) {
      const reportDataInit = initReportEngine(id);
      setReportEngineData(reportDataInit);

      let dataCarStore: TResultImageCoordinatesSchema[] = [];

      if (showOnlyDetections) {
        dataCarStore =
          dataCar.result.detectionsElements.filter(
            (element) => Number(element?.elNumber) === equipmentClassId
          ) || [];
      } else if (showOnlyModerations) {
        dataCarStore =
          dataCar.moderationsElements.filter(
            (element: TResultImageCoordinatesSchema) =>
              Number(element?.elNumber) === equipmentClassId
          ) || [];
      } else {
        dataCarStore = [
          ...dataCar.result.detectionsElements,
          ...(dataCar.moderationsElements || []),
        ];
      }

      const dataDamageStory: TResultImageCoordinatesSchema[] = [
        ...dataCar.result.detectionsDamagesDetected,
        ...(dataCar.moderationsDamages || []),
      ];

      setDataAnnotation([...dataCarStore]);
      setDataAnnotationDamage([...dataDamageStory]);

      dataCarStore?.map((data) => {
        setListElement((prev) => [
          ...prev,
          {
            elNumber: data.elNumber,
            color: getColorByValue(
              data.elNumber.toString(),
              true,
              equipmentsSelectOptions,
              allDamagesSelectOptions
            ),
          },
        ]);
      });

      dataDamageStory?.map((data) => {
        setListDamage((prev) => [
          ...prev,
          {
            elNumber: data.elNumber,
            color: getColorByValue(
              data.elNumber.toString(),
              false,
              equipmentsSelectOptions,
              allDamagesSelectOptions
            ),
          },
        ]);
      });
    }
  }, [dataCar, showOnlyDetections, showOnlyModerations, equipmentClassId]);

  useEffect(
    () => () => {
      setListElement([]);
      setListDamage([]);
      setDataAnnotation([]);
      setDataAnnotationDamage([]);
    },
    []
  );

  return {
    dataCar,
    isPending,
    carProcessedNext,
    generateReportData,
  };
};

export default useReportData;
